//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//
import { useSyncExternalStore } from 'react';

/**
 * Progress of a tool-output download, kept outside the row (spec 007). A collapsed row can
 * unmount while its download is still running; keeping the state here means re-opening the
 * row shows "Downloading…" (no second download can start) and keeps an error message.
 * Keyed by conversation and tool call, so two chats never share a state.
 */
export interface DownloadState {
  busy: boolean;
  problem: string;
}

const IDLE: DownloadState = { busy: false, problem: '' };
const states = new Map<string, DownloadState>();
const listeners = new Set<() => void>();

export const downloadKey = (
  conversationId: string | undefined,
  toolCallId: string,
): string => `${conversationId ?? ''}\u0000${toolCallId}`;

export const getDownloadState = (key: string): DownloadState =>
  states.get(key) ?? IDLE;

export const setDownloadState = (key: string, next: DownloadState): void => {
  if (!next.busy && !next.problem) {
    states.delete(key);
  } else {
    states.set(key, next);
  }
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useDownloadState = (key: string): DownloadState =>
  useSyncExternalStore(
    subscribe,
    () => getDownloadState(key),
    () => IDLE,
  );

/** For tests: forget every state. */
export const resetDownloadStates = (): void => {
  states.clear();
  listeners.forEach((listener) => listener());
};
